import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { categories, movies, getFeaturedMovies, getHotMovies, getTopRanked, siteInfo, faqData } from '@/mocks/movies';

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  const featuredMovies = getFeaturedMovies();
  const hotMovies = getHotMovies();
  const topRanked = getTopRanked();

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '51影院',
    url: 'https://www.51cinema.com',
    description: siteInfo.description,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressCountry: 'CN',
      streetAddress: siteInfo.address,
    },
  };

  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '51影院',
    url: 'https://www.51cinema.com',
    description: siteInfo.description,
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.51cinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[420px] md:h-[550px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20stunning%20cinematic%20abstract%20composition%20with%20warm%20golden%20and%20deep%20red%20gradient%2C%20movie%20theater%20curtains%20dramatically%20lit%2C%20soft%20bokeh%20lights%20floating%2C%20luxurious%20velvet%20texture%2C%20elegant%20cinema%20atmosphere%2C%20no%20people%2C%20artistic%20photography%20with%20rich%20contrast%20and%20dreamy%20depth%20of%20field&width=1800&height=800&seq=hero-bg-cinema&orientation=landscape"
            alt="51影院 - 免费高清在线观影"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center w-full px-4 md:px-6 max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black font-heading text-background-50 mb-4 md:mb-6 leading-tight">
                汇聚全网好片，<br className="md:hidden" />
                <span className="text-primary-400">免费高清</span>在线看
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-6 md:mb-8 leading-relaxed">
                6080影院 · 小a影院 · 8090影院 · 4480影院 优质资源一站聚合，超清画质免广告，流畅不缓冲
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/movie"
                  className="px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  立即观影
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 bg-background-50/20 text-background-50 font-semibold rounded-full border border-background-50/30 hover:bg-background-50/30 transition-all duration-200 whitespace-nowrap cursor-pointer text-sm md:text-base"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </header>

        <main>
          {/* Category Navigation */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10" aria-label="影视分类导航">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-center gap-2 md:gap-3 flex-wrap">
                {categories.map(cat => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 font-medium text-sm hover:bg-primary-500 hover:text-background-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className={`${cat.icon} text-base`}></i>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Movies */}
          <section className="w-full px-4 md:px-6 py-6 md:py-8" aria-label="精选推荐">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  精选推荐
                </h2>
                <Link to="/category/movie" className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {featuredMovies.slice(0, 5).map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Hot Now */}
          <section className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-100" aria-label="热播推荐">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900">
                  热播推荐
                </h2>
                <Link to="/category/tv" className="text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {hotMovies.slice(0, 6).map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* Rankings */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10" aria-label="排行榜">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                热门排行
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-bold text-sm"><i className="ri-fire-line"></i></span>
                    <h3 className="font-heading font-bold text-foreground-900">本周热度榜</h3>
                  </div>
                  <div className="flex flex-col gap-2">
                    {topRanked.slice(0, 5).map((movie, index) => (
                      <Link
                        key={movie.id}
                        to={`/article/${movie.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                      >
                        <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${
                          index === 0 ? 'bg-accent-500 text-background-50' :
                          index === 1 ? 'bg-secondary-500 text-background-50' :
                          index === 2 ? 'bg-primary-500 text-background-50' :
                          'bg-background-300 text-foreground-600'
                        }`}>{index + 1}</span>
                        <span className="flex-1 font-medium text-sm text-foreground-900 line-clamp-1">{movie.title}</span>
                        <span className="text-xs text-accent-500 font-semibold">{movie.rating}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-bold text-sm"><i className="ri-star-line"></i></span>
                    <h3 className="font-heading font-bold text-foreground-900">高分推荐</h3>
                  </div>
                  <div className="flex flex-col gap-2">
                    {[...movies].sort((a, b) => b.rating - a.rating).slice(0, 5).map((movie, index) => (
                      <Link
                        key={movie.id}
                        to={`/article/${movie.id}`}
                        className="flex items-center gap-3 p-3 rounded-lg hover:bg-background-200 transition-colors duration-200 cursor-pointer"
                      >
                        <span className={`w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${
                          index === 0 ? 'bg-accent-500 text-background-50' :
                          index === 1 ? 'bg-secondary-500 text-background-50' :
                          index === 2 ? 'bg-primary-500 text-background-50' :
                          'bg-background-300 text-foreground-600'
                        }`}>{index + 1}</span>
                        <span className="flex-1 font-medium text-sm text-foreground-900 line-clamp-1">{movie.title}</span>
                        <span className="text-xs text-accent-500 font-semibold">{movie.rating}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Why Choose Us */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100" aria-label="为什么选择51影院">
            <div className="max-w-7xl mx-auto text-center">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-3">
                为什么选择<strong>51影院</strong>？
              </h2>
              <p className="text-foreground-600 mb-8 max-w-2xl mx-auto text-sm md:text-base">
                聚合6080影院、小a影院、8090影院、4480影院等优质资源
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {[
                  { icon: 'ri-4k-line', title: '超清画质', desc: '1080P及以上画质，清晰流畅观影体验' },
                  { icon: 'ri-close-circle-line', title: '免广告', desc: '纯净观影，不被任何广告打扰' },
                  { icon: 'ri-speed-up-line', title: '不缓冲', desc: '高速CDN加速，即点即播' },
                  { icon: 'ri-refresh-line', title: '每日更新', desc: '同步6080影院等站，资源最新' },
                ].map((item, idx) => (
                  <div key={idx} className="bg-background-50 rounded-lg p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">{item.title}</h3>
                    <p className="text-foreground-600 text-xs">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="w-full px-4 md:px-6 py-8 md:py-10" aria-label="最新更新">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                最新更新
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                {movies.sort((a, b) => b.id - a.id).slice(0, 12).map(movie => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          </section>

          {/* SEO Content & FAQ */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-label="常见问题">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-8 text-center">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((faq, idx) => (
                  <details key={idx} className="bg-background-50 rounded-lg p-4 md:p-5 cursor-pointer group">
                    <summary className="font-medium text-foreground-900 text-sm md:text-base list-none flex items-center justify-between">
                      {faq.question}
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 ml-3"></i>
                    </summary>
                    <p className="mt-3 text-foreground-600 text-sm leading-relaxed">{faq.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}